import { QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  AvailabilityDto,
  FoodDto,
  PurchaseDto,
  getPurchase,
  loadAvailabilities,
  loadAvailabilitiesByPurchases,
  loadFood,
  loadFoods,
  loadFoodsByUnits,
  loadPurchases,
  loadPurchasesByFoods,
  loadPurchasesByUnits,
} from "../api";
import { createBatchCache } from "./batchCache";
import { queryKeys } from "./queries";
import { collectFoodIds, collectPurchaseIds } from "../utils/foodIdUtils";
import { JustIntake, MealWithIntakes } from "../data/plan";
import { MealWithIngredients } from "../data/mealWithIngredients";
import { Intake } from "../data/intake";
import { collectPurchaseIdsFromIntakes } from "../utils/foodIdUtils";

// ─── Foods ────────────────────────────────────────────────────────────────────

export const foodCache = createBatchCache<FoodDto>("food", [
  {
    name: "byId",
    key: queryKeys.food,
    idsOf: (food) => [food.id],
    load: (ids) => loadFoods({ body: ids }).then((r) => r.data),
    loadOne: (id) =>
      loadFood({ path: { foodId: String(id) } }).then((r) => r.data),
  },
  {
    name: "byUnit",
    key: (id) => ["food", "byUnit", id],
    idsOf: (food) => food.foodUnits.map((foodUnit) => foodUnit.id),
    load: (ids) => loadFoodsByUnits({ body: ids }).then((r) => r.data),
  },
]);

export const useFoodsByIds = (ids: number[], unitIds: number[] = []) =>
  foodCache.useByIds([ids, unitIds]);

/** Non-suspending Variante für zustandsbehaftete Container. */
export const useFoodsByIdsAsync = (ids: number[], unitIds: number[] = []) =>
  foodCache.useByIdsAsync([ids, unitIds]);

/** Suspender Lookup eines Foods über die Food-Id. */
export const useFood = (id: number | null | undefined) =>
  foodCache.useOne(0, id);

/** Suspender Lookup eines Foods über eine FoodUnit-Id. */
export const useFoodByUnit = (unitId: number | null | undefined) =>
  foodCache.useOne(1, unitId);

/** Non-suspending Lookups (für zustandsbehaftete Container). */
export const useFoodAsync = (id: number | null | undefined) =>
  foodCache.useOneAsync(0, id);

export const useFoodByUnitAsync = (unitId: number | null | undefined) =>
  foodCache.useOneAsync(1, unitId);

export const ensureFoods = (
  client: QueryClient,
  ids: number[],
  unitIds: number[] = [],
) =>
  Promise.all([
    foodCache.ensure(client, 0, ids),
    foodCache.ensure(client, 1, unitIds),
  ]).then(() => undefined);

export const getFood = (client: QueryClient, id: number | undefined) =>
  foodCache.get(client, 0, id ?? -1);

export const getFoodByUnit = (
  client: QueryClient,
  unitId: number | undefined,
) => foodCache.get(client, 1, unitId ?? -1);

export const seedFood = (client: QueryClient, food: FoodDto) =>
  foodCache.seed(client, food);

// ─── Purchases ────────────────────────────────────────────────────────────────

export const purchaseCache = createBatchCache<PurchaseDto>("purchase", [
  {
    name: "byId",
    key: queryKeys.purchase,
    idsOf: (purchase) => [purchase.id],
    load: (ids) => loadPurchases({ body: ids }).then((r) => r.data),
    loadOne: (id) =>
      getPurchase({ path: { purchaseId: String(id) } }).then((r) => r.data),
  },
  {
    name: "byFood",
    key: (id) => ["purchase", "byFood", id],
    idsOf: (purchase) => (purchase.foodId ? [purchase.foodId] : []),
    load: (ids) => loadPurchasesByFoods({ body: ids }).then((r) => r.data),
  },
  {
    name: "byUnit",
    key: (id) => ["purchase", "byUnit", id],
    idsOf: (purchase) => purchase.purchaseUnits.map((unit) => unit.id),
    load: (ids) => loadPurchasesByUnits({ body: ids }).then((r) => r.data),
  },
]);

export const usePurchasesByIds = (
  ids: number[],
  foodIds: number[] = [],
  unitIds: number[] = [],
) => purchaseCache.useByIds([ids, foodIds, unitIds]);

/** Non-suspending Variante für zustandsbehaftete Container. */
export const usePurchasesByIdsAsync = (
  ids: number[],
  foodIds: number[] = [],
  unitIds: number[] = [],
) => purchaseCache.useByIdsAsync([ids, foodIds, unitIds]);

/** Suspender Lookup eines Purchases über die Purchase-Id. */
export const usePurchase = (id: number | null | undefined) =>
  purchaseCache.useOne(0, id);

/** Suspender Lookup aller Purchases eines Foods. */
export const usePurchasesByFood = (foodId: number | null | undefined) =>
  purchaseCache.useMany(1, foodId);

/** Non-suspending Lookups (für zustandsbehaftete Container). */
export const usePurchaseAsync = (id: number | null | undefined) =>
  purchaseCache.useOneAsync(0, id);

export const usePurchasesByFoodAsync = (foodId: number | null | undefined) =>
  purchaseCache.useManyAsync(1, foodId);

/** Suspender Lookup eines Purchases über eine PurchaseUnit-Id. */
export const usePurchaseByUnit = (unitId: number | null | undefined) =>
  purchaseCache.useOne(2, unitId);

export const ensurePurchases = (
  client: QueryClient,
  ids: number[],
  foodIds: number[] = [],
  unitIds: number[] = [],
) =>
  Promise.all([
    purchaseCache.ensure(client, 0, ids),
    purchaseCache.ensure(client, 1, foodIds),
    purchaseCache.ensure(client, 2, unitIds),
  ]).then(() => undefined);

export const getPurchaseById = (client: QueryClient, id: number | undefined) =>
  purchaseCache.get(client, 0, id ?? -1);

export const getPurchasesByFood = (
  client: QueryClient,
  foodId: number | undefined,
) => purchaseCache.getMany(client, 1, foodId ?? -1);

export const getPurchasesByUnit = (
  client: QueryClient,
  unitId: number | undefined,
) => purchaseCache.getMany(client, 2, unitId ?? -1);

export const seedPurchase = (client: QueryClient, purchase: PurchaseDto) =>
  purchaseCache.seed(client, purchase);

// ─── Availabilities ───────────────────────────────────────────────────────────

export const availabilityCache = createBatchCache<AvailabilityDto>(
  "availability",
  [
    {
      name: "byId",
      key: (id) => ["availability", "byId", id],
      idsOf: (availability) => [availability.id],
      load: (ids) => loadAvailabilities({ body: ids }).then((r) => r.data),
    },
    {
      name: "byPurchase",
      key: (id) => ["availability", "byPurchase", id],
      idsOf: (availability) =>
        availability.purchaseId != null ? [availability.purchaseId] : [],
      load: (ids) =>
        loadAvailabilitiesByPurchases({ body: ids }).then((r) => r.data),
    },
  ],
);

export const useAvailabilitiesByPurchases = (purchaseIds: number[]) =>
  availabilityCache.useByIds([[], purchaseIds]);

/** Non-suspending Variante (für zustandsbehaftete Container). */
export const useAvailabilitiesByPurchasesAsync = (purchaseIds: number[]) =>
  availabilityCache.useByIdsAsync([[], purchaseIds]);

/** Suspender Lookup der Verfügbarkeiten eines Purchases. */
export const useAvailabilitiesByPurchase = (
  purchaseId: number | null | undefined,
) => availabilityCache.useMany(1, purchaseId);

export const ensureAvailabilities = (
  client: QueryClient,
  purchaseIds: number[],
) => availabilityCache.ensure(client, 1, purchaseIds);

export const getAvailabilitiesByPurchase = (
  client: QueryClient,
  purchaseId: number | undefined,
) => availabilityCache.getMany(client, 1, purchaseId ?? -1);

export const seedAvailability = (
  client: QueryClient,
  availability: AvailabilityDto,
) => availabilityCache.seed(client, availability);

type MealLike = MealWithIntakes | MealWithIngredients;

/**
 * Lädt alle Purchases, die für die übergebenen Meals benötigt werden:
 * per Purchase-Id (Zutaten) und per Food-Id (Produkt-Alternativen).
 */
export function useMealPurchases(meals: MealLike[]): PurchaseDto[] {
  const purchaseIds = useMemo(() => collectPurchaseIds(meals), [meals]);
  const baseFoodIds = useMemo(() => collectFoodIds(meals), [meals]);
  const purchases = usePurchasesByIds(purchaseIds, baseFoodIds);
  const purchaseFoodIds = useMemo(
    () => [
      ...new Set(
        purchases
          .map((purchase) => purchase.foodId)
          .filter((id): id is number => id != null && id > 0),
      ),
    ],
    [purchases],
  );
  const allFoodIds = useMemo(
    () => [...new Set([...baseFoodIds, ...purchaseFoodIds])],
    [baseFoodIds, purchaseFoodIds],
  );
  return usePurchasesByIds(purchaseIds, allFoodIds);
}

export function useJustIntakePurchases(intakes: JustIntake[]): PurchaseDto[] {
  return usePurchasesWithFoodIds(intakes.map((i) => i.purchaseUnitPurchaseId));
}

function usePurchasesWithFoodIds(purchaseIds: number[]) {
  const purchases = usePurchasesByIds(purchaseIds);
  const foodIds = useMemo(
    () => [
      ...new Set(
        purchases
          .map((purchase) => purchase.foodId)
          .filter((id): id is number => id != null && id > 0),
      ),
    ],
    [purchases],
  );
  return usePurchasesByIds(purchaseIds, foodIds);
}

/**
 * Non-suspending Varianten für zustandsbehaftete Container (Seite, Drawer mit
 * State). Suspense würde dort den State verwerfen und neu mounten.
 */
export function useMealPurchasesAsync(meals: MealLike[]): PurchaseDto[] {
  const purchaseIds = useMemo(() => collectPurchaseIds(meals), [meals]);
  const baseFoodIds = useMemo(() => collectFoodIds(meals), [meals]);
  const purchases = usePurchasesByIdsAsync(purchaseIds, baseFoodIds);
  const purchaseFoodIds = useMemo(
    () => [
      ...new Set(
        purchases
          .map((purchase) => purchase.foodId)
          .filter((id): id is number => id != null && id > 0),
      ),
    ],
    [purchases],
  );
  const allFoodIds = useMemo(
    () => [...new Set([...baseFoodIds, ...purchaseFoodIds])],
    [baseFoodIds, purchaseFoodIds],
  );
  return usePurchasesByIdsAsync(purchaseIds, allFoodIds);
}

export function useJustIntakePurchasesAsync(
  intakes: JustIntake[],
): PurchaseDto[] {
  const purchaseIds = useMemo(
    () => intakes.map((i) => i.purchaseUnitPurchaseId),
    [intakes],
  );
  const purchases = usePurchasesByIdsAsync(purchaseIds);
  const foodIds = useMemo(
    () => [
      ...new Set(
        purchases
          .map((purchase) => purchase.foodId)
          .filter((id): id is number => id != null && id > 0),
      ),
    ],
    [purchases],
  );
  return usePurchasesByIdsAsync(purchaseIds, foodIds);
}

/**
 * Wie `useMealPurchasesAsync`, gibt aber zusätzlich `isLoading` zurück. Damit
 * können Konsumenten „lädt noch" von „gibt es nicht" unterscheiden und während
 * des Ladens auf Lookup-Misses/leere Nährwerte verzichten.
 */
export function useMealPurchasesState(meals: MealLike[]): {
  purchases: PurchaseDto[];
  isLoading: boolean;
} {
  const purchaseIds = useMemo(() => collectPurchaseIds(meals), [meals]);
  const baseFoodIds = useMemo(() => collectFoodIds(meals), [meals]);
  const base = usePurchasesByIdsState(purchaseIds, baseFoodIds);
  const purchaseFoodIds = useMemo(
    () => [
      ...new Set(
        base.data
          .map((purchase) => purchase.foodId)
          .filter((id): id is number => id != null && id > 0),
      ),
    ],
    [base.data],
  );
  const allFoodIds = useMemo(
    () => [...new Set([...baseFoodIds, ...purchaseFoodIds])],
    [baseFoodIds, purchaseFoodIds],
  );
  const result = usePurchasesByIdsState(purchaseIds, allFoodIds);
  return {
    purchases: result.data,
    isLoading: base.isLoading || result.isLoading,
  };
}

/** Wie `useJustIntakePurchasesAsync`, zusätzlich mit `isLoading`. */
export function useJustIntakePurchasesState(intakes: JustIntake[]): {
  purchases: PurchaseDto[];
  isLoading: boolean;
} {
  const purchaseIds = useMemo(
    () => intakes.map((i) => i.purchaseUnitPurchaseId),
    [intakes],
  );
  const base = usePurchasesByIdsState(purchaseIds);
  const foodIds = useMemo(
    () => [
      ...new Set(
        base.data
          .map((purchase) => purchase.foodId)
          .filter((id): id is number => id != null && id > 0),
      ),
    ],
    [base.data],
  );
  const result = usePurchasesByIdsState(purchaseIds, foodIds);
  return {
    purchases: result.data,
    isLoading: base.isLoading || result.isLoading,
  };
}

/** Lädt alle Purchases für die übergebenen Intakes (inkl. Alternativen). */
export function useIntakePurchases(intakes: Intake[]): PurchaseDto[] {
  const purchaseIds = useMemo(
    () => collectPurchaseIdsFromIntakes(intakes),
    [intakes],
  );
  return usePurchasesWithFoodIds(purchaseIds);
}

/** Lädt alle Purchases zu den übergebenen Food-Ids. */
export const useFoodPurchases = (foodIds: number[]) =>
  usePurchasesByIds([], foodIds);

/** Non-suspending Variante (für zustandsbehaftete Container). */
export const useFoodPurchasesAsync = (foodIds: number[]) =>
  usePurchasesByIdsAsync([], foodIds);

/**
 * Non-suspending Lookups **mit** Lade-Flag. Sicherheitsnetz, damit Konsumenten
 * „lädt noch" von „gibt es nicht" unterscheiden und einen Ladeplatzhalter statt
 * eines leeren Zustands rendern können.
 */
export const useFoodState = (id: number | null | undefined) =>
  foodCache.useOneState(0, id);

export const useFoodByUnitState = (unitId: number | null | undefined) =>
  foodCache.useOneState(1, unitId);

export const useFoodsByIdsState = (ids: number[], unitIds: number[] = []) =>
  foodCache.useByIdsState([ids, unitIds]);

export const usePurchaseState = (id: number | null | undefined) =>
  purchaseCache.useOneState(0, id);

export const usePurchasesByFoodState = (foodId: number | null | undefined) =>
  purchaseCache.useManyState(1, foodId);

export const usePurchasesByIdsState = (
  ids: number[],
  foodIds: number[] = [],
  unitIds: number[] = [],
) => purchaseCache.useByIdsState([ids, foodIds, unitIds]);

/**
 * Synchrone Foods/Purchases-Lookups für eine Menge von Meals aus dem
 * Query-Cache. Nur sinnvoll **nach** `ensureFoods`/`ensurePurchases`, damit alle
 * benötigten Einträge geladen sind. Wird z. B. beim Verzehr genutzt, damit
 * `createCustomIntakes` nicht wegen noch nicht geladener (asynchroner) Daten
 * leere Intakes erzeugt.
 */
export function getConsumeLookups(
  client: QueryClient,
  meals: MealWithIngredients[],
  /** Zusätzliche FoodUnit-Ids (z. B. gewählte Rezept-Alternativen). */
  extraFoodUnitIds: number[] = [],
): { purchases: PurchaseDto[]; foods: FoodDto[] } {
  const allFoodIds = new Set<number>(collectFoodIds(meals));
  const purchaseIds = collectPurchaseIds(meals);

  extraFoodUnitIds.forEach((unitId) => {
    const food = getFoodByUnit(client, unitId);
    if (food) allFoodIds.add(food.id);
  });

  const purchaseMap = new Map<number, PurchaseDto>();
  purchaseIds.forEach((id) => {
    const purchase = getPurchaseById(client, id);
    if (purchase) purchaseMap.set(purchase.id, purchase);
  });
  allFoodIds.forEach((foodId) => {
    getPurchasesByFood(client, foodId).forEach((purchase) =>
      purchaseMap.set(purchase.id, purchase),
    );
  });
  const purchases = [...purchaseMap.values()];

  purchases.forEach((purchase) => {
    if (purchase.foodId && purchase.foodId > 0) {
      allFoodIds.add(purchase.foodId);
    }
  });
  const foods = [...allFoodIds]
    .map((id) => getFood(client, id))
    .filter((food): food is FoodDto => food != null);

  return { purchases, foods };
}

export const invalidateFoods = (client: QueryClient) => {
  foodCache.reset();
  return client.invalidateQueries({ queryKey: ["food"] });
};

export const invalidatePurchases = (client: QueryClient) => {
  purchaseCache.reset();
  return client.invalidateQueries({ queryKey: ["purchase"] });
};

export const invalidateAvailabilities = (client: QueryClient) => {
  availabilityCache.reset();
  return client.invalidateQueries({ queryKey: ["availability"] });
};
